import React, { useState, useContext } from 'react';
import PresetDndContext from '../common/DnD/PresetDndContext';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import NewOption from './NewOption';
import Icon from '../common/Icon';
import { OptionsContext, FieldContext } from '../../hooks/Contexts';
import OptionListItem from './OptionListItem';
import CalendarEventModal from '../metadata/CalendarEventModal';
import { handleSortUpdate } from '../common/DnD/sortUpdater';
import { sortByAlpha, sortable, dragSort } from '../../helpers/sorters';
import { handleUpdateField } from '../fields/constants/handleField';

const OptionsIndex = ({ allOptions }) => {
  const { field, setField } = useContext(FieldContext);
  const isAlphaSort = field.alpha;
  const orderedListClass = isAlphaSort ? ' option-tiles alpha' : ' ui-sortable';
  const determineSort = (optionState) => {
    return isAlphaSort
      ? sortByAlpha(optionState, 'name')
      : sortable(optionState);
  };
  const [options, setOptions] = useState(determineSort(allOptions));
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [calEventOption, setCalEventOption] = useState(null);

  const refreshOption = (options, id, freshOpt) => {
    let untouched = options.filter((opt) => opt.id != id);
    return setOptions(determineSort([...untouched, freshOpt]));
  };

  const toggleSort = async () => {
    let newSort = { alpha: !field.alpha };
    let updated = await handleUpdateField(field.id, newSort);
    if (updated.ok) {
      setOptions((opts) => {
        return updated.data.alpha ? sortByAlpha(opts, 'name') : sortable(opts);
      });
      // set field last in case field.alpha disables sorting
      setField(updated.data);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      let sort = options.map((opt) => opt.id).indexOf(over.id);
      let backup = [...options];
      setOptions((opts) => dragSort(opts, active, over));
      let sorted = await handleSortUpdate('options', active.id, sort);
      if (!sorted.ok) setOptions(backup);
    }
  };

  const openCalModal = (option) => {
    setCalEventOption(option);
    setShowCalendarModal(true);
  };

  const toggleCalendarModal = () => {
    setShowCalendarModal(false);
    setCalEventOption(null);
  };

  return (
    <section className='options'>
      <h2>
        Options
        <div className='tile-sort'>
          {isAlphaSort ? (
            <Icon
              title='Restore to Manual Sort'
              onClick={toggleSort}
              icon='sort-amount-asc'
            />
          ) : (
            <Icon
              title='Sort by Alpha'
              onClick={toggleSort}
              icon='sort-alpha-asc'
            />
          )}
        </div>
      </h2>
      <OptionsContext.Provider
        value={{
          options,
          setOptions,
          determineSort,
          openCalModal,
          refreshOption,
        }}
      >
        <NewOption isAlphaSort={isAlphaSort} setOptions={setOptions} />
        <ol className={'orderable' + orderedListClass}>
          <PresetDndContext handleDragEnd={handleDragEnd}>
            {options.length ? (
              <SortableContext
                items={options}
                strategy={verticalListSortingStrategy}
              >
                {options.map((opt, i) => {
                  return (
                    <OptionListItem option={opt} key={opt.id} id={opt.id} />
                  );
                })}
              </SortableContext>
            ) : (
              <li>No options added yet...</li>
            )}
          </PresetDndContext>
        </ol>
        {calEventOption && (
          <CalendarEventModal
            showCalendarModal={showCalendarModal}
            toggleCalendarModal={toggleCalendarModal}
            option={calEventOption}
          />
        )}
      </OptionsContext.Provider>
    </section>
  );
};

export default OptionsIndex;
